import { useContext, useState as classicUseState } from 'react';
import { useState } from '@hookstate/core';
import { DeleteDialog, GlobalMenu } from '@components';
import { patchUsers } from '@services/queries/users/patchUsers/patchUsers';
import { useQueryClient, useMutation } from 'react-query';
import { toast } from 'react-toast';
import { IUser } from '@models/User.model';
import { NavigationContext } from '@layout/navigation/navigation';
import { InviteUserRole } from '@models/Invite.model';
import { Container } from './CurrentUsersMenu.styles';
import { EditUserDetails } from './EditUserDetails/EditUserDetails';

interface ICurrentUsersMenu {
  user: IUser;
}

const CurrentUsersMenu = ({ user }: ICurrentUsersMenu) => {
  const [editUser, setEditUser] = classicUseState(false);
  const { role } = useContext(NavigationContext);

  const queryClient = useQueryClient();

  const ToastText = () => {
    if (user.blocked) return 'User Activated';
    return 'User Deactivated';
  };

  const openDeleteDialog = useState(false);
  const openUnblockDialog = useState(false);
  const mutationPatchUser = useMutation(patchUsers, {
    onSuccess: () => {
      queryClient.invalidateQueries(['membersAuth0']);
      toast.success(ToastText());
    },
  });

  return (
    <Container>
      <GlobalMenu
        align="end"
        title="Options"
        itemList={[
          {
            disabled: ![InviteUserRole.ADMIN].includes(role),
            disableToolTip: [InviteUserRole.ADMIN].includes(role),
            toolTipText: 'Only admins can edit user information.',
            toolTipId: 'editUserTooltip',
            label: 'Edit user',
            iconName: 'edit',
            onClick: () => {
              setEditUser(true);
            },
          },
          {
            disabled: ![InviteUserRole.ADMIN].includes(role),
            disableToolTip: [InviteUserRole.ADMIN].includes(role),
            toolTipText: 'Only admins can deactivate/activate users.',
            toolTipId: 'manageUserTooltip',
            label: user.blocked ? 'Activate' : 'Deactivate',
            iconName: user.blocked ? 'new-person' : 'blocked-person',
            onClick: () => {
              if (user.blocked) return openUnblockDialog.set(true);
              return openDeleteDialog.set(true);
            },
          },
        ]}
      />

      <DeleteDialog
        text="This will prevent this user from accessing Beam"
        buttonText="Deactivate"
        hideCancel
        open={openDeleteDialog}
        onDelete={() => {
          mutationPatchUser.mutate({
            id: user.id,
            blocked: true,
          });
          openDeleteDialog.set(false);
        }}
      />

      <DeleteDialog
        text="This will allow this user to log into Beam again"
        buttonText="Activate"
        backgroundColor="#FF9900"
        hideCancel
        open={openUnblockDialog}
        onDelete={() => {
          mutationPatchUser.mutate({
            id: user.id,
            blocked: false,
          });
          openUnblockDialog.set(false);
        }}
      />

      <EditUserDetails
        open={editUser}
        onClose={() => setEditUser(false)}
        user={user}
      />
    </Container>
  );
};

export { CurrentUsersMenu };
